/*
 * The capability grant — what a rendering context is allowed to do.
 *
 * A widget holds NO credentials. It runs in an origin-isolated iframe under
 * `default-src 'none'` and reaches everything through the host's postMessage
 * bridge, so the host is the only thing that decides what is permitted. That
 * makes "who is viewing" a parameter rather than an assumption: the SAME
 * renderer serves an authenticated operator, a read-only shared link, and a
 * share that may press exactly two named buttons — the only difference is the
 * grant handed to the host.
 *
 * This is why the grant is explicit from the first commit. Threading a
 * capability through later means auditing every call site that assumed an
 * ambient logged-in user, which is the expensive version of this.
 */

/** A symbol the widgets in this context may READ. Encoded-id, not display path. */
export interface ReadGrant {
  gatewayId: string;
  controllerId: string;
  /** Encoded symbol ids (pathenc). Empty = nothing readable in this controller. */
  symbolIds: string[];
}

/** A model command this context may EXECUTE, named explicitly. */
export interface CommandGrant {
  gatewayId: string;
  modelId: string;
  /**
   * Command names permitted, matched exactly. NEVER a wildcard: a shared
   * dashboard that may press "acknowledge" must not thereby be able to press
   * "stop". The reserved lifecycle names (start/stop/pause/resume/reset) are
   * permitted here only if the sharer named them.
   */
  commands: string[];
}

/** A symbol this context may WRITE, named explicitly. */
export interface WriteGrant {
  gatewayId: string;
  controllerId: string;
  /**
   * The plugin that owns this controller. Writes are plugin-routed, and a
   * widget has no business knowing which plugin backs a controller — so the
   * grant carries it. It also means a grant cannot be pointed at a controller
   * whose owning plugin the granter could not name.
   */
  pluginId: string;
  /** Encoded symbol ids permitted for write. Exact match, never a prefix. */
  symbolIds: string[];
}

export interface CapabilityGrant {
  /** Human-facing reason this grant exists; surfaced in the UI and in audit. */
  readonly kind: 'session' | 'share';
  /** Reads permitted. A grant with no reads renders a dashboard of empty widgets. */
  readonly reads: readonly ReadGrant[];
  /** Writes permitted. EMPTY for a read-only share — the default. */
  readonly writes: readonly WriteGrant[];
  /** Model commands permitted. EMPTY for a read-only share — the default. */
  readonly commands: readonly CommandGrant[];
  /**
   * Wall-clock expiry (ms since epoch), or null for a live app session that
   * ends when the tab does. A share ALWAYS carries one: the host stops
   * serving data at expiry without waiting for the server to refuse.
   */
  readonly expiresAtMs: number | null;

  /**
   * SESSION ONLY. Reads on these gateways are permitted without being named.
   *
   * This exists because a signed-in operator viewing their own dashboard in the
   * app already reaches every symbol on that gateway through the symbol grid -
   * the backend, its org scoping and its entitlements are the authority for what
   * they may see, and enumerating it here would be a second, weaker copy of a
   * decision already made correctly elsewhere.
   *
   * A SHARE must never carry this. The whole point of a share is that it is
   * NARROWER than the session that made it, so `grantFromSnapshot` never sets
   * it and a test asserts that it cannot. Anything set here is checked against
   * `kind === 'session'` before it has any effect, so a forged share grant
   * carrying it still reads nothing.
   */
  readonly openGateways?: readonly string[];
}

/** The grant an unauthenticated or mis-scoped context gets: nothing. */
export const DENY_ALL: CapabilityGrant = {
  kind: 'share',
  reads: [],
  writes: [],
  commands: [],
  expiresAtMs: 0,
};

export function isExpired(g: CapabilityGrant, nowMs: number): boolean {
  return g.expiresAtMs !== null && nowMs >= g.expiresAtMs;
}

/**
 * Read check. Fails closed: an unknown gateway/controller, or a symbol the
 * grant does not name, is refused. There is deliberately no prefix or wildcard
 * match — a subtree grant would let a renamed symbol silently widen access.
 */
export function mayRead(
  g: CapabilityGrant,
  nowMs: number,
  gatewayId: string,
  controllerId: string,
  symbolId: string,
): boolean {
  if (isExpired(g, nowMs)) return false;
  // The session escape hatch, gated on kind so a share cannot claim it.
  if (g.kind === 'session' && g.openGateways?.includes(gatewayId)) return true;
  return g.reads.some(
    (r) => r.gatewayId === gatewayId && r.controllerId === controllerId && r.symbolIds.includes(symbolId),
  );
}

/**
 * The grant a signed-in operator gets for a gateway they already have access
 * to. Reads ride the backend's own authority; writes and commands still have to
 * be named, because "can see it" and "can move it" are different questions and
 * a dashboard should not quietly answer the second.
 */
export function sessionGrant(gatewayId: string): CapabilityGrant {
  return {
    kind: 'session',
    reads: [],
    writes: [],
    commands: [],
    expiresAtMs: null,
    openGateways: [gatewayId],
  };
}

/**
 * Resolve a write to the plugin that must carry it out, or null if the grant
 * does not permit it. Returning the route rather than a boolean keeps the
 * permission check and the routing decision in ONE place — a separate lookup
 * afterwards is a second source of truth about who owns the controller.
 */
export function writeRoute(
  g: CapabilityGrant,
  nowMs: number,
  gatewayId: string,
  controllerId: string,
  symbolId: string,
): { pluginId: string } | null {
  if (isExpired(g, nowMs)) return null;
  const w = g.writes.find(
    (x) => x.gatewayId === gatewayId && x.controllerId === controllerId && x.symbolIds.includes(symbolId),
  );
  return w ? { pluginId: w.pluginId } : null;
}

export function mayWrite(
  g: CapabilityGrant,
  nowMs: number,
  gatewayId: string,
  controllerId: string,
  symbolId: string,
): boolean {
  return writeRoute(g, nowMs, gatewayId, controllerId, symbolId) !== null;
}

export function mayCommand(
  g: CapabilityGrant,
  nowMs: number,
  gatewayId: string,
  modelId: string,
  command: string,
): boolean {
  if (isExpired(g, nowMs)) return false;
  return g.commands.some(
    (c) => c.gatewayId === gatewayId && c.modelId === modelId && c.commands.includes(command),
  );
}

/** Narrow a grant to reads only — what "share this dashboard read-only" produces. */
export function readOnly(g: CapabilityGrant): CapabilityGrant {
  return { ...g, writes: [], commands: [] };
}
